import { CloudAnomalyAlert } from "../../types/alert";
import { Card, CardContent, CardHeader, CardTitle } from "../../../../ui/common-ui/card";
import { useMemo, useState } from 'react';
import { BarChart3, Users, Shield, ChevronDown, ChevronUp, Brain, BookOpen, Route, AlertTriangle } from 'lucide-react';

interface AnalyticsData {
  topAlerts: Record<string, number>;
  topAlertTypes: Record<string, number>;
  topRiskyEntities: Record<string, number>;
  topAffectedResources: Record<string, number>;
}

interface AlertAnalyticsProps {
  alerts: CloudAnomalyAlert[];
  onAlertTypeClick?: (alertType: string) => void;
  onAlertTypeModeClick?: (alertType: string) => void;
  onEntityClick?: (entity: string) => void;
  analyticsData?: AnalyticsData;
}

const analyticsCardClass =
  "rounded-2xl border border-border/50 shadow-lg shadow-sky-500/5 bg-card transition-all hover:border-primary/30 hover:shadow-[0_4px_16px_hsl(var(--primary)/0.06)] hover:-translate-y-px";

const analyticsRowClass =
  "flex items-center justify-between p-3 rounded-2xl bg-card transition-all duration-300 border border-border/50 cursor-pointer hover:border-primary/30 hover:shadow-[0_4px_16px_hsl(var(--primary)/0.06)] hover:-translate-y-px group";

export const AlertAnalytics = ({ alerts, onAlertTypeClick, onAlertTypeModeClick, onEntityClick, analyticsData }: AlertAnalyticsProps) => {
  const [showAllAlerts, setShowAllAlerts] = useState(false);
  const [showAllEntities, setShowAllEntities] = useState(false);
  const [showAllResources, setShowAllResources] = useState(false);

  const getProgressColor = (percentage: number) => {
    if (percentage >= 70) return "bg-primary";
    if (percentage >= 40) return "bg-primary/70";
    if (percentage >= 20) return "bg-primary/50";
    return "bg-primary/30";
  };

  const truncateMiddle = (text: string, maxLength: number = 50) => {
    if (text.length <= maxLength) return text;
    const halfLength = Math.floor((maxLength - 3) / 2);
    return text.substring(0, halfLength) + '...' + text.substring(text.length - halfLength);
  };

  const analytics = useMemo(() => {
    const total = alerts.length;
    if (total === 0) {
      return {
        alertTypes: [],
        topAlerts: [],
        topEntities: [],
        topResources: [],
        allAlerts: [],
        allEntities: [],
        allResources: [],
      };
    }

    // Fixed Alert Types (BEHAVIOUR, RULE, SEQUENTIAL)
    const typeCounts = alerts.reduce((acc, alert) => {
      acc[alert.alertType] = (acc[alert.alertType] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const alertTypes = [
      {
        type: "BEHAVIOUR",
        label: "Behaviour",
        icon: Brain,
        count: typeCounts["BEHAVIOUR"] || 0,
        percentage: Number((((typeCounts["BEHAVIOUR"] || 0) / total) * 100).toFixed(1))
      },
      {
        type: "RULE",
        label: "Rule",
        icon: BookOpen,
        count: typeCounts["RULE"] || 0,
        percentage: Number((((typeCounts["RULE"] || 0) / total) * 100).toFixed(1))
      },
      {
        type: "SEQUENTIAL",
        label: "Sequential",
        icon: Route,
        count: typeCounts["SEQUENTIAL"] || 0,
        percentage: Number((((typeCounts["SEQUENTIAL"] || 0) / total) * 100).toFixed(1))
      },
    ];

    // Top Alerts (by title)
    const alertTitles = alerts.reduce((acc, alert) => {
      acc[alert.title] = (acc[alert.title] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const allAlerts = Object.entries(alertTitles)
      .map(([title, count]) => ({
        title,
        displayTitle: truncateMiddle(title),
        fullTitle: title,
        count,
        percentage: Number(((count / total) * 100).toFixed(1))
      }))
      .sort((a, b) => b.count - a.count);

    const topAlerts = allAlerts.slice(0, 5);

    // Top risky entities
    const entities = alerts.reduce((acc, alert) => {
      acc[alert.entity] = (acc[alert.entity] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const allEntities = Object.entries(entities)
      .map(([entity, count]) => ({
        entity: truncateMiddle(entity),
        fullEntity: entity,
        count,
        percentage: Number(((count / total) * 100).toFixed(1))
      }))
      .sort((a, b) => b.count - a.count);

    const topEntities = allEntities.slice(0, 5);

    // Top affected resources - extract from attackPath
    const resources = alerts.reduce((acc, alert) => {
      // Get resources from attackPath
      if (alert.attackPath) {
        Object.values(alert.attackPath).forEach((step) => {
          if (step.resource) {
            const resourceName = step.resource.split('/').pop() || step.resource;
            acc[resourceName] = (acc[resourceName] || 0) + 1;
          }
        });
      }
      // Also include affectedResources if available
      alert.affectedResources?.forEach((resource) => {
        const resourceName = resource.split('/').pop() || resource;
        acc[resourceName] = (acc[resourceName] || 0) + 1;
      });
      return acc;
    }, {} as Record<string, number>);

    const allResources = Object.entries(resources)
      .map(([resource, count]) => ({
        resource: truncateMiddle(resource),
        fullResource: resource,
        count,
        percentage: Number(((count / total) * 100).toFixed(1))
      }))
      .sort((a, b) => b.count - a.count);

    const topResources = allResources.slice(0, 5);

    return { alertTypes, topAlerts, topEntities, topResources, allAlerts, allEntities, allResources };
  }, [alerts]);

  return (
    <div className="space-y-6">
      {/* 3. Top Risky Entities */}
      <Card className={analyticsCardClass}>
        <CardHeader className="pb-4">
          <div className="flex items-center gap-3">
            <div className="h-8 w-1 bg-primary rounded-full" />
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <Users className="h-5 w-5 text-primary" />
              </div>
              <div>
                <CardTitle className="text-lg font-bold text-foreground tracking-tight">
                  Top Risky Entities
                </CardTitle>
                <p className="text-xs text-muted-foreground">Most involved entities in alerts</p>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {(showAllEntities ? analytics.allEntities : analytics.topEntities).map((item, index) => (
              <div
                key={index}
                className={analyticsRowClass}
                onClick={() => onEntityClick?.(item.fullEntity)}
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                    <span className="text-sm font-bold text-primary">{index + 1}</span>
                  </div>
                  <span className="text-sm font-medium text-foreground truncate group-hover:text-primary transition-colors" title={item.fullEntity}>
                    {item.entity}
                  </span>
                </div>
                <div className="flex-shrink-0 ml-4 w-32">
                  <div className="relative h-5 bg-muted rounded-full overflow-hidden">
                    <div
                      className={`h-full ${getProgressColor(item.percentage)} transition-all duration-500`}
                      style={{ width: `${item.percentage}%` }}
                    />
                    <span className="absolute inset-0 flex items-center justify-center text-xs font-semibold text-foreground">
                      {item.percentage}%
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {analytics.allEntities.length > 5 && (
            <button
              onClick={() => setShowAllEntities(!showAllEntities)}
              className="w-full mt-4 flex items-center justify-center gap-2 py-2 text-sm font-medium text-primary hover:text-primary/80 transition-colors"
            >
              {showAllEntities ? (
                <>
                  <ChevronUp className="h-4 w-4" />
                  Show Less
                </>
              ) : (
                <>
                  <ChevronDown className="h-4 w-4" />
                  Show More ({analytics.allEntities.length - 5} more)
                </>
              )}
            </button>
          )}
        </CardContent>
      </Card>

      {/* 4. Top Affected Resources */}
      <Card className={analyticsCardClass}>
        <CardHeader className="pb-4">
          <div className="flex items-center gap-3">
            <div className="h-8 w-1 bg-primary rounded-full" />
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <Shield className="h-5 w-5 text-primary" />
              </div>
              <div>
                <CardTitle className="text-lg font-bold text-foreground tracking-tight">
                  Top Affected Resources
                </CardTitle>
                <p className="text-xs text-muted-foreground">Most targeted cloud resources</p>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {(showAllResources ? analytics.allResources : analytics.topResources).map((item, index) => (
              <div key={index} className={analyticsRowClass}>
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="text-sm font-bold text-primary">{index + 1}</span>
                  </div>
                  <span className="text-sm font-medium text-foreground truncate" title={item.fullResource}>
                    {item.resource}
                  </span>
                </div>
                <div className="flex-shrink-0 ml-4 w-32">
                  <div className="relative h-5 bg-muted rounded-full overflow-hidden">
                    <div
                      className={`h-full ${getProgressColor(item.percentage)} transition-all duration-500`}
                      style={{ width: `${item.percentage}%` }}
                    />
                    <span className="absolute inset-0 flex items-center justify-center text-xs font-semibold text-foreground">
                      {item.percentage}%
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {analytics.allResources.length > 5 && (
            <button
              onClick={() => setShowAllResources(!showAllResources)}
              className="w-full mt-4 flex items-center justify-center gap-2 py-2 text-sm font-medium text-primary hover:text-primary/80 transition-colors"
            >
              {showAllResources ? (
                <>
                  <ChevronUp className="h-4 w-4" />
                  Show Less
                </>
              ) : (
                <>
                  <ChevronDown className="h-4 w-4" />
                  Show More ({analytics.allResources.length - 5} more)
                </>
              )}
            </button>
          )}
        </CardContent>
      </Card>

      {/* 5. Top Alerts (by title) */}
      <Card className={analyticsCardClass}>
        <CardHeader className="pb-4">
          <div className="flex items-center gap-3">
            <div className="h-8 w-1 bg-primary rounded-full" />
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <AlertTriangle className="h-5 w-5 text-primary" />
              </div>
              <div>
                <CardTitle className="text-lg font-bold text-foreground tracking-tight">
                  Top Alerts
                </CardTitle>
                <p className="text-xs text-muted-foreground">Most frequent alert titles</p>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {(showAllAlerts ? analytics.allAlerts : analytics.topAlerts).map((item, index) => (
              <div
                key={index}
                className={analyticsRowClass}
                onClick={() => onAlertTypeClick?.(item.fullTitle)}
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                    <span className="text-sm font-bold text-primary">{index + 1}</span>
                  </div>
                  <span className="text-sm font-medium text-foreground truncate group-hover:text-primary transition-colors" title={item.fullTitle}>
                    {item.displayTitle}
                  </span>
                </div>
                <div className="flex-shrink-0 ml-4 w-32">
                  <div className="relative h-5 bg-muted rounded-full overflow-hidden">
                    <div
                      className={`h-full ${getProgressColor(item.percentage)} transition-all duration-500`}
                      style={{ width: `${item.percentage}%` }}
                    />
                    <span className="absolute inset-0 flex items-center justify-center text-xs font-semibold text-foreground">
                      {item.percentage}%
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {analytics.allAlerts.length > 5 && (
            <button
              onClick={() => setShowAllAlerts(!showAllAlerts)}
              className="w-full mt-4 flex items-center justify-center gap-2 py-2 text-sm font-medium text-primary hover:text-primary/80 transition-colors"
            >
              {showAllAlerts ? (
                <>
                  <ChevronUp className="h-4 w-4" />
                  Show Less
                </>
              ) : (
                <>
                  <ChevronDown className="h-4 w-4" />
                  Show More ({analytics.allAlerts.length - 5} more)
                </>
              )}
            </button>
          )}
        </CardContent>
      </Card>

      {/* 6. Top Alert Types (Fixed: Behaviour, Rule, Sequential) */}
      <Card className={analyticsCardClass}>
        <CardHeader className="pb-4">
          <div className="flex items-center gap-3">
            <div className="h-8 w-1 bg-primary rounded-full" />
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <BarChart3 className="h-5 w-5 text-primary" />
              </div>
              <div>
                <CardTitle className="text-lg font-bold text-foreground tracking-tight">
                  Top Alert Types
                </CardTitle>
                <p className="text-xs text-muted-foreground">Distribution by detection type</p>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {analytics.alertTypes.map((item, index) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.type}
                  className={analyticsRowClass}
                  onClick={() => onAlertTypeModeClick?.(item.type)}
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                      <Icon className="h-4 w-4 text-primary" />
                    </div>
                    <span className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">
                      {item.label}
                    </span>
                  </div>
                  <div className="flex-shrink-0 ml-4 w-32">
                    <div className="relative h-5 bg-muted rounded-full overflow-hidden">
                      <div
                        className={`h-full ${getProgressColor(item.percentage)} transition-all duration-500`}
                        style={{ width: `${Math.max(item.percentage, 5)}%` }}
                      />
                      <span className="absolute inset-0 flex items-center justify-center text-xs font-semibold text-foreground">
                        {item.percentage}%
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
